const Elm = require('./frontend-app').Elm
const elmPkgJsInit = require('./elm-pkg-js-includes').init

// ['subscribe' ,'unsubscribe' ,'ports'].map(btoa)
// ["c3Vic2NyaWJl", "dW5zdWJzY3JpYmU=", "cG9ydHM="]
const subscribe = ex('c3Vic2NyaWJl')
const unsubscribe = ex('dW5zdWJzY3JpYmU=')
const ports = ex('cG9ydHM=')

const platformVersion = 'fe-1.2.0-0.19.1'

const wsUrl = "wss://" + window.location.host + "/_w";

// WebSocket.OPEN. Spelled out rather than read off the constructor so it still
// works when the socket is one handed over from a previous app during an upgrade.
const SOCKET_OPEN = 1

// ── ToBackend delivery ──────────────────────────────────────────────────────
// `Lamdera.sendToBackend` is documented as delivered, and ws.send() does not
// provide that on its own: a socket that dies takes whatever it had not
// transmitted with it, with no throw and no error — and the close event that
// would tell us arrives later still. So we hold every ToBackend until the
// backend confirms it, and retransmit anything unconfirmed on the next socket.
//
// Retransmitting blind would turn a lost message into a duplicated one, so the
// backend has to be able to recognise what it already has. It counts the frames
// it takes from us and tells us the running total; we tell it, as each socket
// opens, the index our next frame will carry. See runtime/js/delivery.js for
// that side and the protocol in full.
//
// Retransmission only starts once the backend has acked at least once. An older
// backend never does, and cannot deduplicate either, so against one we stay
// exactly as fire-and-forget as the runtime has always been.
//
// This lives in the page and nowhere else. A reload is a new app with a fresh
// model, so replaying a queue into it would be wrong, and the backend forgets a
// client that stays away too long regardless.
const WIRE_TO_BACKEND = 0 // client -> server  [0][version][ToBackend]
const DELIVERY_RESUME = 3 // client -> server  [3][deliveryId:16][resumeFrom:u32]
const DELIVERY_ACK = 4    // server -> client  [4][received:u32]
// A backstop, not a design point: if this much is unconfirmed something is badly
// wrong, and losing the oldest loudly beats taking the tab down.
const DELIVERY_MAX_BYTES = 4 * 1024 * 1024

function newDeliveryState() {
  const id = new Uint8Array(16)
  crypto.getRandomValues(id)
  return {
    id: id,
    // ToBackends this page has accepted from Elm. The n-th is index n, and the
    // backend numbers them the same way, which is what lets it dedupe.
    sentSeq: 0,
    // Highest index we will never send again: confirmed by the backend, or
    // released because this backend does not confirm. The next frame we send is
    // settledThrough + 1, which is what a resume frame announces.
    settledThrough: 0,
    // Highest index handed to the CURRENT socket. Reset back to settledThrough
    // whenever a new socket is opened, which is what schedules the retransmit.
    transmittedThrough: 0,
    unacked: [],  // [{ seq, buffer }], ascending, seq > settledThrough
    bytes: 0,     // size of unacked, against DELIVERY_MAX_BYTES
    acking: false, // has this backend ever acked? only then may we retransmit
    // Frontends that lack this numbered analytics frames as well. See init().
    toBackendsOnly: true
  }
}

function deliveryResumeFrame(delivery) {
  const frame = new Uint8Array(1 + 16 + 4)
  frame[0] = DELIVERY_RESUME
  frame.set(delivery.id, 1)
  new DataView(frame.buffer).setUint32(17, delivery.settledThrough + 1)
  return frame
}

// There is a lot of complexity in detecting device & system types accurately, but for our purposes the
// following seems to be sufficient to at least limit the scope of the websocket bug handling to iOS
// Inspecting navigator values on devices/simulators is best to determine what identifies them
const iOS = navigator.userAgent.includes('AppleWebKit') && "ontouchend" in document

function dlx(...args) { console.log(new Date().toISOString(), ...args) }

if (window.lamdera && window.lamdera.u2) {
  // We're upgrading an app using the old upgrade system.
  // The old system results in exceptions being thrown due to taking over the DOM
  // from the previous app, which still has event listeners to the old app.
  // By clearing the body first we avoid those exceptions, but we also lose any
  // DOM states such as scroll position, but it’s better than nothing.
  const init = Elm.LFR.init
  Elm.LFR.init = function (args) {
    Elm.LFR.init = init
    while (document.body.firstChild) {
      document.body.removeChild(document.body.firstChild)
    }
    return init(args)
  }
  window.lamdera.u2(Elm.LFR)
} else if (window.lamdera && window.lamdera.u3) {
  // We're upgrading an app using the new upgrade system.

  const newVersion = window.lamdera.conf.appVersion + 1

  const upgrade = () => {
    delete window.lamdera.onUpgradeSignal

    if (window.lamdera.conf.reloadOnMigration) {
      window.location.reload()
    }

    const currentApp = window.app
    const currentAppPorts = currentApp[ports]

    dlx(`[v${newVersion}] starting upgrade to v${newVersion}`)

    var bufferedMsgs = []
    var bufferRedirectedMsg = function (payload) {
      bufferedMsgs.push(payload)
    }

    // On receive pipe Model to app+1 & setup
    var onOldModelDumped = function (bytes) {
      // dlx(`[v${newVersion}] got onOldModelDumped callback`)
      currentAppPorts.fecfs[unsubscribe](onOldModelDumped)

      // Stop inputs to the old app. Already pending Cmds are still handled.
      // Newer apps have `.detachView` (as well as `.die` for compatibility).
      // Older apps only have `.die`.
      if (currentApp.detachView) {
        currentApp.detachView()
      } else {
        currentApp.die()
        // Make the DOM from an older app ready to be virtualized by a newer app.
        for (const child of document.body.getElementsByTagName('*')) {
          delete child.elm_event_node_ref
          child.setAttribute('data-elm', '')
        }
      }

      var upgradeData = window.lamdera.u3()
      init({
        currentVersion: newVersion,
        connected: upgradeData.connected,
        maybeOldWs: upgradeData.ws,
        pendingDelivery: upgradeData.pendingDelivery,
        initArgs: { flags: { b: bytes, bv: newVersion } },
      })

      var nextAppPorts = window.app[ports]
      var handleRedirectedMsg = nextAppPorts.rrm.send
      for (let i = 0; i < bufferedMsgs.length; i++) {
        handleRedirectedMsg(bufferedMsgs[i])
      }
      bufferedMsgs.length = 0
      currentAppPorts.rm[unsubscribe](bufferRedirectedMsg)
      currentAppPorts.rm[subscribe](handleRedirectedMsg)

      const modelTransferDuration = Date.now() - upgradeStartedTime
      dlx(`[v${newVersion}] upgrade total pause time ${modelTransferDuration} milliseconds`)

      const stopSeconds = 60
      const stopTimeout = stopSeconds * 1000
      dlx(`[v${newVersion}] is now in control, stopping v${window.lamdera.conf.appVersion} in ${stopSeconds}s`)
      window.lamdera.conf.appVersion = newVersion

      // I tried keeping track of which Cmds are in progress so we know exactly
      // when to kill the old app, but that proved to be very difficult. The old app
      // doesn’t do any harm though, it just wastes a bit of memory, so we could just
      // as well do it after enough time for most HTTP requests to finish has passed.
      setTimeout(() => {
        // Let the old app be garbage collected. If any Cmds are still in progress,
        // they will be ignored (and console.warn:ed).
        // Newer apps have `.stop`, older apps have `.bury`.
        if (currentApp.stop) {
          currentApp.stop()
        } else {
          currentApp.bury()
        }
        currentAppPorts.rm.unsubscribe(handleRedirectedMsg)
        dlx(`[v${newVersion}] message redirection closed down, buried v${newVersion - 1}`)
      }, stopTimeout) // 60 seconds
    }
    currentAppPorts.fecfs[subscribe](onOldModelDumped)

    // The `feeum` call below is going to trigger `onOldModelDumped`
    // immediately, but outgoing ports in Elm always happen after a `setTimeout`
    // (otherwise you don't have time to do `app.ports.myPort.subscribe()` if
    // you trigger a port in `init`). Because of that we must subscribe to
    // redirected messages before we call `feeum`, and before we have the new app,
    // which means that we have to buffer the messages for a (very) short while.
    currentAppPorts.rm[subscribe](bufferRedirectedMsg)

    // Send app stop & dump (app will start passing encoded Msgs & fire createFinalSnapshot)
    const upgradeStartedTime = Date.now()
    currentAppPorts.feeum.send(null)
  }

  if (window.lamdera.onUpgradeSignal === "ready") {
    // The signal was received before the new frontend was loaded.
    dlx(`[v${newVersion}] loaded, upgrade signal already received, upgrading...`)
    upgrade()
  } else {
    dlx(`[v${newVersion}] loaded, waiting for upgrade signal or 60s timeout...`)
    // Wait for the signal to continue the upgrade to be received.
    // If the signal hasn't been received after a while, proceed anyway.
    const timeoutId = setTimeout(upgrade, 60 * 1000) // 1 minute
    window.lamdera.onUpgradeSignal = () => {
      clearTimeout(timeoutId)
      upgrade()
    }
  }
} else {
  // Otherwise, this is the first boot, so we do the base setup for this app and all future apps (i.e. Evergreen deploys)

  window["lamdera"] = {}
  window['lamdera']['conf'] = { appId: 'at-chat', appVersion: 420, reloadOnMigration: false, hb: true }

  var currentVersion = window.lamdera.conf.appVersion

  init({
    currentVersion: currentVersion,
    connected: false,
    maybeOldWs: undefined,
    initArgs: { flags: { b: null, bv: 0 }},
  })
}

function init({
  currentVersion,
  connected,
  maybeOldWs,
  pendingDelivery,
  initArgs,
}) {
  // Carried across an Evergreen upgrade by u3(), queue and all. A fresh state
  // here would drop whatever the previous app had unconfirmed — Elm has already
  // let go of those, so nothing else would ever resend them. Older frontends'
  // u3() returns no such field, in which case we simply start clean.
  //
  // A queue from a frontend that also numbered analytics frames is out of step
  // with the backend's count, and resuming it would resend ToBackends the backend
  // already has. The skew is what kept those messages queued, so they almost
  // certainly arrived: start clean under a new id instead, and announce it on the
  // handed-over socket below.
  const staleDelivery = pendingDelivery !== undefined && !pendingDelivery.toBackendsOnly
  const delivery = pendingDelivery && !staleDelivery ? pendingDelivery : newDeliveryState()
  // Set when we open a socket the backend has not been told about yet.
  let resumeDue = false
  // Frames other than ToBackends, waiting for a live socket. Not numbered: the
  // backend only counts ToBackends, so these stay fire-and-forget as always.
  const untracked = []

  // A brand new socket has transmitted nothing, so everything still unconfirmed
  // is due to go again, and the backend needs to be told where we are picking up.
  const startNewSocketDelivery = () => {
    delivery.transmittedThrough = delivery.settledThrough
    resumeDue = true
  }

  const sendToBackend = (bytes) => {
    // LFR sends an analytics frame through this port on every URL change. If we
    // numbered it, we would run one ahead of the backend's count per navigation,
    // and every reconnect would resend that many of our latest ToBackends.
    if (bytes.getUint8(0) !== WIRE_TO_BACKEND) {
      untracked.push(bytes.buffer)
      flushToBackends()
      return
    }
    delivery.sentSeq += 1
    delivery.unacked.push({ seq: delivery.sentSeq, buffer: bytes.buffer })
    delivery.bytes += bytes.buffer.byteLength
    capUnacked()
    flushToBackends()
  }

  const flushToBackends = function() {
    // `connected` lags the socket: it only flips on the close EVENT, which the
    // browser dispatches as a task some time after readyState leaves OPEN. A
    // send in that window does not throw — the browser silently discards it —
    // so anything we hand over here would vanish. Waiting for a live socket
    // costs nothing now that unsent messages are simply unconfirmed ones.
    if (!connected || ws.readyState !== SOCKET_OPEN) return;
    while (untracked.length > 0) {
      try {
        ws.send(untracked[0])
      } catch (e) {
        break;
      }
      untracked.shift()
    }
    for (let i = 0; i < delivery.unacked.length; i++) {
      const pending = delivery.unacked[i]
      if (pending.seq <= delivery.transmittedThrough) continue;
      try {
        ws.send(pending.buffer)
      } catch (e) {
        // iOS throws here on a socket that reports itself open but is not (the
        // Safari restoration bug terminate() also works around). Stop: this one
        // and everything after it stay unconfirmed and go on the next socket.
        dlx(`[v${currentVersion}] send failed, leaving ToBackend #${pending.seq} for the next connection`, e)
        break;
      }
      delivery.transmittedThrough = pending.seq
    }
    if (!delivery.acking) {
      // This backend has never confirmed anything, so it cannot recognise a
      // retransmit either and resending would risk applying a message twice.
      // Let them go once transmitted, exactly as the runtime always has.
      settleThrough(delivery.transmittedThrough)
    }
  }

  // Forget everything up to and including `seq`: it will never be sent again.
  const settleThrough = (seq) => {
    if (seq <= delivery.settledThrough) return;
    delivery.settledThrough = seq
    while (delivery.unacked.length > 0 && delivery.unacked[0].seq <= seq) {
      delivery.bytes -= delivery.unacked.shift().buffer.byteLength
    }
    if (delivery.transmittedThrough < seq) delivery.transmittedThrough = seq
  }

  const capUnacked = () => {
    while (delivery.bytes > DELIVERY_MAX_BYTES && delivery.unacked.length > 0) {
      const dropped = delivery.unacked[0]
      dlx(`[v${currentVersion}] ⚠️ giving up on unconfirmed ToBackend #${dropped.seq}: over ${DELIVERY_MAX_BYTES} bytes are still unconfirmed`)
      settleThrough(dropped.seq)
    }
  }

  // The backend has taken `count` ToBackends from us in total.
  const onDeliveryAck = (count) => {
    // The first ack is also how we learn this backend speaks the protocol, and
    // therefore that retransmitting to it is safe.
    delivery.acking = true
    settleThrough(count)
  }

  var currentApp = Elm.LFR.init(initArgs)
  window.app = currentApp

  // We won't be initializing the app again. Explicitly deleting Elm from the
  // exports frees much more memory for some reason.
  delete Elm.LFR

  var currentAppPorts = currentApp[ports]
  currentAppPorts.dl[subscribe](dlx)
  currentAppPorts.stb[subscribe](sendToBackend)

  const msgHandlers = makeMsgHandlers(currentApp, currentVersion, onDeliveryAck)

  elmPkgJsInit(currentApp)

  // Experimental elm-pkg-js packages
  // @STUB to be injected by build script based on actual installed
  // elm packages with JS in `elm-pkg-js` format that are known/approved
  // const appElmJsPkgs = {
  //   '77b330c4aba4088b667a780eca5737c1991341a0': require('./ext/77b330c4aba4088b667a780eca5737c1991341a0.js'),
  // }
  // window.debug = appElmJsPkgs

  // Experimental: bind elm-pkg-js modules' js
  // for (var pkgId in appElmJsPkgs) {
  //   if (appElmJsPkgs.hasOwnProperty(pkgId)) {
  //     appElmJsPkgs[pkgId].init(currentApp)
  //   }
  // }

  let ws, noActivity, noPong, reconnectTimer, wsUnsubscribes = []

  // @ISSUE Because our custom websocket mechanism requires liveness of the backend JS thread,
  // if the thread gets blocked by work that's longer than these timeouts, the frontend has no way
  // of knowing whether it actually needs to reconnect or if the backend is just busy.
  // So for now we have to bump these timeouts until we figure out some way around both problems,
  // which I fear we may be hitting some sort of CAP theorem tradeoff potentially...
  const wsNoActivityTimeout = 30_000 // Bump up from 5s to 30s for now...
  const wsPongTimeout = 1_000
  const wsReconnectTimeout = 2_000
  const pingPayload = Uint8Array.from([2,1]) // msgType = 2, version = 1

  // Because of the Safari bug, we need to implement a client->server heartbeat as
  // well as we cannot rely on the onclose to guarantee that we have an active connection
  // and the existing server->client heartbeat frames in the WS protocol are not observable in browsers
  // See https://github.com/enisdenjo/graphql-ws/discussions/290
  const bumpWsActivityTimeout = () => {
    if (!window.lamdera.conf.hb) return; // Heartbeats not supported yet by the currently running backend for this app (be < v12)
    // Firstly we have a bumpable timeout layer, which optimises against useless heartbeats
    // by bumping the wsNoActivityTimeout any time we receive a WS message from the backend
    clearTimeout(noPong)
    clearTimeout(noActivity)
    // If we're in a disconnected/inactive state, don't set new timeouts, just wait for reconnect/visibilitychange to do its thing
    if (!connected || document.visibilityState === 'hidden') return;
    noActivity = setTimeout(pingBackendServer, wsNoActivityTimeout)
  }
  const pingBackendServer = () => {
    // Once we _haven't_ heard from the backend server within the last wsNoActivityTimeout
    // we send a ping and set a timeout for the pong to come back within wsPongTimeout
    if (!connected) return;
    ws.send(pingPayload)
    noPong = setTimeout(terminateHungConnection, wsPongTimeout)
  }
  const terminateHungConnection = () => {
    // Now we haven't heard back from the backend in an additional wsPongTimeout period
    // so this is highly likely a hung connection, so we simulate a close event as per graphql-ws approach
    connected = false
    terminate(4499, `Reaping connection with no pong after ${wsPongTimeout}ms`);
  }

  const adoptedOldWs = connected && maybeOldWs
  ws = adoptedOldWs ? maybeOldWs : new WebSocket(wsUrl)
  // A handed-over socket keeps the delivery position it already had — the
  // previous app transmitted on it and the backend has been counting.
  if (!adoptedOldWs) startNewSocketDelivery()
  else if (staleDelivery) resumeDue = true
  // Frames must arrive as ArrayBuffers, not Blobs. A Blob has to be read before
  // it can be handed to Elm, and in Firefox concurrent reads do not finish in
  // the order they were started, so ToFrontend messages reach the app out of
  // order. See makeMsgHandlers below.
  ws.binaryType = 'arraybuffer'

  const onWs = (event, handler) => {
    // Bind the socket now. Reading the outer `ws` when the unsubscribe finally
    // runs would detach the listener from whichever socket is current by then,
    // leaving the old one still wired up and the new one deaf.
    const socket = ws
    wsUnsubscribes.push(() => socket.removeEventListener(event, handler))
    socket.addEventListener(event, handler)
  }

  const wsUnsubscribeAll = () => {
    for (const unsubscribe of wsUnsubscribes) unsubscribe()
  }

  const setupWs = () => {
    const onopen = () => {
      connected = true
      dlx(`[v${currentVersion}] connected`)
      bumpWsActivityTimeout()
      if (resumeDue) {
        // Announce the index our next ToBackend will carry, so the backend can
        // drop the ones it already has out of the retransmit that follows.
        try {
          ws.send(deliveryResumeFrame(delivery))
          resumeDue = false
        } catch (e) {
          // Leave resumeDue set; this socket is not usable and the reconnect
          // that follows will announce again.
          dlx(`[v${currentVersion}] could not announce delivery resume`, e)
        }
      }
      flushToBackends()
    };
    if (connected && maybeOldWs) {
      onopen()
    } else {
      onWs('open', onopen)
    }
    maybeOldWs = undefined

    onWs('message', e => {
      bumpWsActivityTimeout()
      msgHandlers(e.data)
    })

    onWs('close', () => {
      connected = false
      dlx(`[v${currentVersion}] closed`)
      reconnect()
    })
  }

  const reconnect = () => {
    // Drop any reconnect already scheduled, so overlapping triggers (a close
    // event and a visibilitychange, say) can't each open a socket. Today they
    // happen to collapse because both use the same delay and the older timer
    // fires first and clears the newer one; that stops being true the moment
    // this timeout gains jitter or backoff, and two live sockets means two
    // clientIds for one browser.
    clearTimeout(reconnectTimer)
    reconnectTimer = setTimeout(reconnectImmediately, wsReconnectTimeout)
  }

  const reconnectImmediately = () => {
    clearTimeout(reconnectTimer)
    // Detach from the outgoing socket before adopting the new one, so a late
    // event on it can't reach handlers that now believe they speak for the
    // replacement.
    wsUnsubscribeAll()
    wsUnsubscribes.length = 0
    ws = new WebSocket(wsUrl)
    ws.binaryType = 'arraybuffer'
    startNewSocketDelivery()
    setupWs()
  }

  const terminate = (code, reason) => {
    const e = { code, reason, wasClean: false };
    console.log('terminate', e);
    wsUnsubscribeAll() // Avoid triggering the close event handler
    try {
      // This will throw on iOS when the connection is broken due to the safari restoration bug
      ws.close(e.code || 1e3, e.reason);
    } catch (e) {
      console.log('terminate error', e);
    }
    connected = false;
    reconnectImmediately();
  }

  setupWs()

  const removeVisibilityChangeListener = afterLoadedVisibilityChange(() => {
    dlx(`[v${currentVersion}] visibilitychange`, document.visibilityState);
    if (document.visibilityState === 'visible') {
      // Because of the safari disconnect bug, we need to make sure
      // that when the page is active again we terminate our possibly
      // invalid connection and reconnect. There's no way to detect it it seems(?)
      // Idea: what about testing a send with try catch and if it fails, terminate?
      // if (iOS) terminate(4499, `Manually terminating possibly broken iOS connection`)
      if (iOS && connected) {
        try {
          ws.send(pingPayload)
        } catch (e) {
          dlx(e);
          terminate(4499, `Manually terminating broken iOS connection`)
        }
      }
      if (!connected) reconnect()
    }
    // Re-arm (or tear down) the heartbeat for the visibility we just moved to.
    // bumpWsActivityTimeout is otherwise only reached from onopen and from an
    // incoming message, and it deliberately declines to arm anything while
    // hidden — so without this the FIRST time the tab is backgrounded the
    // heartbeat stops for good, and a connection that dies afterwards is never
    // detected. The tab then sits on a dead socket, silently buffering nothing,
    // until the backend happens to send something (which it can't).
    bumpWsActivityTimeout()
  });

  window.lamdera.u3 = function() {
    clearTimeout(noActivity)
    clearTimeout(noPong)
    clearTimeout(reconnectTimer)

    removeVisibilityChangeListener()

    currentAppPorts.stb[unsubscribe](sendToBackend)
    currentAppPorts.dl[unsubscribe](dlx)

    wsUnsubscribeAll()

    return {
      ws: ws,
      connected: connected,
      // Hand the whole delivery state to the next app: the unconfirmed queue,
      // where the numbering is up to, and this page's delivery id. Elm has
      // already let go of those messages, so if we drop them here nothing
      // resends them — and a new id would make the backend forget what it has
      // already taken from us, so the queue would be applied twice.
      pendingDelivery: delivery
    }
  }
}

function ex(s) {
  return atob(s)
}

// fromWireJsonMsg(blob: DataView): { version: number, path?: string }
function fromWireJsonMsg(bytes) {
  // Drop off the first byte indicating the msg type, and parse the remaining as text and JSON
  // (`.size` here used to be a leftover from when this was handed a Blob; on an
  // ArrayBuffer it is undefined, so slice ran to the end by accident anyway.)
  const jsonStr = new TextDecoder().decode(bytes.buffer.slice(1))
  return JSON.parse(jsonStr)
}

function makeMsgHandlers(currentApp, currentVersion, onDeliveryAck) {
  dlx(`[v${currentVersion}] making msg handlers`)
  // Stays synchronous on purpose. The 'message' listener does not await this,
  // so any suspension point in here lets a later frame overtake an earlier one
  // on its way to `rfb.send`, which the app sees as messages arriving out of
  // order. The socket is set to binaryType 'arraybuffer' so there is nothing
  // left to wait for.
  return function(buffer) {
    const bytes = new DataView(buffer)
    const wireMsgType = bytes.getUint8(0)
    switch(wireMsgType) {
      case 0:
        // ToFrontend msg
        currentApp[ports].rfb.send(bytes)
        break;
      case 1:{
        // Backend upgrade pre-load
        const bu_msg = fromWireJsonMsg(bytes)
        dlx(`[v${currentVersion}] received pre-load instruction for v${bu_msg.version}`)

        var head = document.getElementsByTagName('head')[0]
        var js = document.createElement("script")
        js.type = "module"
        js.src = "/" + bu_msg.path
        head.appendChild(js)
        break;
      }
      case 2:{
        // Backend has signalled the pending upgrade should proceed
        const u_msg = fromWireJsonMsg(bytes)
        dlx(`[v${currentVersion}] upgrade signal received for v${u_msg.version}`)
        if (window.lamdera.onUpgradeSignal) {
          // Next frontend is already ready.
          window.lamdera.onUpgradeSignal()
        } else {
          // Signal received before the new frontend loaded.
          window.lamdera.onUpgradeSignal = "ready"
        }
        break;
      }
      case 4:{
        // Cumulative count of ToBackend frames the backend has taken from this
        // page. Everything up to it is confirmed; anything past it is still ours
        // to retransmit. See the delivery notes at the top of this file.
        onDeliveryAck(bytes.getUint32(1))
        break;
      }
      case 3:{
        // Backend has sent a user-land server -> client pong response
        // do nothing as all responses from backend trigger bumpWsActivityTimeout()
        // dlx(`[v${currentVersion}] received pong from backend`)
        break;
      }
      default: dlx(`[v${currentVersion}] unknown wire payload type: ${wireMsgType}`)
    }
  }
}

// Fires a callback when the document is loaded and when the visibility changes,
// thus avoiding the awkward visibility change event on page load
// function afterLoadedVisibilityChange(callback: () => void): void {
function afterLoadedVisibilityChange(callback) {
  // Check if the document is already loaded
  if (document.readyState === 'complete' || document.readyState === 'interactive') {
    // If the document is already loaded, directly add the visibility change listener
    document.addEventListener('visibilitychange', callback);
  } else {
    // If the document is still loading, first wait for it to complete
    var onLoad = () => {
      window.removeEventListener('load', onLoad);
      // After the page is loaded, add the visibility change listener
      document.addEventListener('visibilitychange', callback);
    };
    window.addEventListener('load', onLoad);
  }
  return () => document.removeEventListener('visibilitychange', callback);
}
